import { useEffect, useMemo, useState } from 'react';
import { intelDealBoard, intelPaperBook, intelPaperPosition, type DealBoard, type PaperBook, type PaperPositionDetail } from '@aithora/shared';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { EmptyState } from '../../ui/states';
import type { IntelTab, TabProps } from './common';
import { GC, M, pc, type Deal } from './model';
import { peerOf, usePeerStaffing } from './staffing';
import { PortfolioMap, type PfPin } from './deals/PortfolioMap';
import './deals/deals.css';
import { BuildLabel } from '../../ui/buildLabels';
import { occupancyNote, useReferenceBenchmarks } from '../../referenceBenchmarks';

const MINUS = '−';
const CAPS = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const COLS = 'display:grid;grid-template-columns:minmax(0,2.2fr) 44px 48px 40px 70px 70px 70px 72px 62px 56px 56px 62px 120px;gap:8px;';
const BTN_O = 'height:26px;display:inline-flex;align-items:center;padding:0 9px;border-radius:6px;border:1px solid rgba(47,92,151,.35);font-size:14px;font-weight:600;color:#2F5C97;text-decoration:none;white-space:nowrap;';
const BTN_S = 'height:26px;display:inline-flex;align-items:center;padding:0 9px;border-radius:6px;background:#2F5C97;font-size:14px;font-weight:600;color:#fff;text-decoration:none;white-space:nowrap;';
const KV = 'display:flex;align-items:baseline;gap:9px;font-size:16px;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.06);';

/** One quarter of a holding's marks since entry: plan (modeled) and marked (realized), $M. */
export interface QuarterMark {
  label: string;
  plan: number;
  real: number;
}

/**
 * A holding's marks from its paper position (GET /paper-positions/:id): every point after
 * entry with a realized value, as realized / modeled. The latest six. None when the position
 * has no realized marks yet: then the holding is marked at entry and the page says so.
 */
export function marksOf(d: PaperPositionDetail | null | undefined): QuarterMark[] {
  const pts = (d?.drift?.series ?? []).filter((x) => x.realizedEv != null && x.modeledEv > 0 && !/pre-entry/i.test(x.label ?? ''));
  return pts.slice(-6).map((x) => ({ label: String(x.label || x.date).replace(' · ', ' '), plan: x.modeledEv / 1e6, real: (x.realizedEv as number) / 1e6 }));
}

/** A holding with its marks, latest mark and plan figures (all MODELED until cost reports load). */
export interface Holding {
  x: Deal;
  /** The anonymised descriptor letter (A, B, C ... in book order). */
  letter: string;
  marks: QuarterMark[];
  /** realized / modeled per quarter, oldest first; empty when marked at entry. */
  drift: number[];
  mark: number;
  /** The plan value now (the latest quarter's modeled value; entry when unmarked). */
  planNow: number;
  /** EBITDA now: the deal card's EBITDA scaled by the latest mark against entry. */
  actE: number;
  planE: number;
}

export function holdingsOf(held: Deal[], details: Record<string, PaperPositionDetail | null>, posByDeal: Record<string, string | undefined>): Holding[] {
  return held.map((x, i) => {
    const pid = posByDeal[x.id];
    const marks = marksOf(pid ? details[pid] : null);
    const drift = marks.map((m) => m.real / m.plan);
    const last = marks[marks.length - 1];
    const mark = last ? last.real : x.entry;
    const planNow = last ? last.plan : x.entry;
    return { x, letter: String.fromCharCode(65 + i), marks, drift, mark, planNow, actE: (x.ebitda * mark) / x.entry, planE: (x.ebitda * planNow) / x.entry };
  });
}

type Att = [number, string, string, string, () => void];

/**
 * Portfolio (V4 aiIsPf, T15447; aiNsVals L30627, rebuilt 2026-10-02): what we hold, rolled up.
 * Holdings (entry, latest mark, gain, % vs plan, debt cover, IRR, a six-quarter plan-vs-actual
 * strip), what needs attention and the tab that fixes it, book value by quarter, the value
 * bridge from entry to today, and where we own. Marks come from each holding's paper position
 * (GET /paper-positions/:id); a holding with no realized mark is shown marked at entry. Debt
 * service is the deal card's bank loan (model.ts). Staffing reads against same-type,
 * same-state peers: there is no federal staffing floor.
 */
export function PortfolioTab(p: TabProps) {
  const held = useMemo(() => p.deals.filter((x) => x.status === 'Acquired'), [p.deals]);
  const book = useQuery<PaperBook>((signal) => intelPaperBook(p.client, { signal }), [p.client]);
  const board = useQuery<DealBoard>((signal) => intelDealBoard(p.client, { signal }), [p.client]);
  const bench = useReferenceBenchmarks(p.client);
  const posByDeal = useMemo(() => {
    const out: Record<string, string | undefined> = {};
    for (const x of held) {
      const pos = book.data?.positions.find((q) => q.status === 'open' && (q.facilityId === x.providerId || (!!x.providerId && q.facilityId.endsWith(':' + x.providerId)) || q.facilityName === x.name));
      out[x.id] = pos?.id;
    }
    return out;
  }, [held, book.data]);
  const [details, setDetails] = useState<Record<string, PaperPositionDetail | null>>({});
  const posKey = Object.values(posByDeal).filter(Boolean).sort().join(',');
  useEffect(() => {
    const ids = posKey ? posKey.split(',') : [];
    if (!ids.length) return;
    const ctrl = new AbortController();
    Promise.all(ids.map((id) => intelPaperPosition(p.client, { params: { id }, signal: ctrl.signal }).then((d) => [id, d] as const, () => [id, null] as const))).then((rows) => {
      if (!ctrl.signal.aborted) setDetails(Object.fromEntries(rows));
    });
    return () => ctrl.abort();
  }, [p.client, posKey]);
  const peers = usePeerStaffing(p.client, held.map((x) => ({ type: x.type, state: x.stt })));

  if (!held.length) return <EmptyState title="Nothing held yet" body="A deal moved to Closed on Tracking joins the portfolio here." />;

  const H = holdingsOf(held, details, posByDeal);
  const sum = (fn: (h: Holding) => number) => H.reduce((s, h) => s + fn(h), 0);
  const pfBeds = sum((h) => h.x.beds);
  const pfEV = sum((h) => h.mark);
  const pfEbitda = sum((h) => h.x.ebitda);
  const pfEntry = sum((h) => h.x.entry);
  const pfOcc = sum((h) => h.x.occ * h.x.beds) / (pfBeds || 1);
  const pfStates = held.map((x) => x.stt).filter((s, i, a) => a.indexOf(s) === i).length;
  const goF = (h: Holding, t: IntelTab) => () => p.go(t, { aiSel: p.deals.indexOf(h.x), aiLastGroup: 'facility', aiLastTab: { ...(p.st.aiLastTab ?? {}), facility: t === 'scenario' || t === 'simulate' ? 'deal' : t } });

  // Needs attention, worst first. Staffing: below the median of the same type in the same state.
  const att: Att[] = [];
  const belowPeer: Holding[] = [];
  H.forEach((h) => {
    const x = h.x;
    const tag = 'Target ' + h.letter;
    const d = h.drift.length ? h.drift[h.drift.length - 1] - 1 : null;
    if (d !== null) {
      let run = 0;
      for (let i = h.drift.length - 1; i >= 0 && h.drift[i] < 1; i--) run++;
      if (d < -0.03) att.push([2, tag, 'EBITDA ' + Math.abs(d * 100).toFixed(1) + '% below plan, ' + run + ' quarter' + (run === 1 ? '' : 's') + ' running', 'Track live', goF(h, 'simulate')]);
      else if (d < 0) att.push([1, tag, 'EBITDA ' + Math.abs(d * 100).toFixed(1) + '% below plan this quarter', 'Track live', goF(h, 'simulate')]);
    }
    const peer = peerOf(peers, x.type, x.stt);
    if (peer && peer.median != null && x.hprd < peer.median) {
      belowPeer.push(h);
      att.push([2, tag, 'Staffing ' + x.hprd.toFixed(2) + ' HPRD, ' + (peer.median - x.hprd).toFixed(2) + ' below the typical ' + peer.cohort, 'Analysis', goF(h, 'target')]);
    }
    if (x.agency > 0.07) att.push([1, tag, 'Agency labor ' + (x.agency * 100).toFixed(0) + '% of labor, target 7%', 'Levers', goF(h, 'scenario')]);
    if (h.actE / x.ds < 1.45) att.push([1, tag, 'Debt cover ' + (h.actE / x.ds).toFixed(2) + '×, HUD refinance needs 1.45×', 'Deal model', goF(h, 'deal')]);
  });
  att.sort((a, b) => b[0] - a[0]);
  const dsT = sum((h) => h.x.ds);
  const eT = sum((h) => h.actE);
  const planET = sum((h) => h.planE);
  const debtT = sum((h) => h.x.debt);

  // Book value by quarter: quarters since entry, summed over the holdings marked that quarter.
  const nQ = Math.max(0, ...H.map((h) => h.marks.length));
  const Q = Array.from({ length: nQ }, (_, q) => {
    const at = H.filter((h) => h.marks[q]);
    return { label: at[0]?.marks[q].label ?? 'Q' + (q + 1), mod: at.reduce((s, h) => s + h.marks[q].plan, 0), real: at.reduce((s, h) => s + h.marks[q].real, 0) };
  });
  const lo = Q.length ? Math.min(...Q.map((r) => Math.min(r.mod, r.real))) * 0.96 : 0;
  const hi = Q.length ? Math.max(...Q.map((r) => Math.max(r.mod, r.real))) : 1;
  const ht = (v: number) => Math.max(6, Math.round(((v - lo) / (hi - lo || 1)) * 64)) + 'px';

  const grow = sum((h) => h.planNow - h.x.entry);
  const vsP = sum((h) => h.mark - h.planNow);
  const bmax = pfEntry + grow + Math.max(0, vsP);
  const bw = (v: number) => Math.max(1, (Math.abs(v) / bmax) * 100).toFixed(1) + '%';
  const bridge: [string, string, number, number, string][] = [
    ['Capital deployed', M(pfEntry), 0, pfEntry, '#5B6B7C'],
    ['Planned growth, ' + nQ + ' qtrs', '+' + M(grow), pfEntry, grow, '#2F5C97'],
    ['Versus plan', (vsP >= 0 ? '+' : MINUS) + M(Math.abs(vsP)), vsP >= 0 ? pfEntry + grow : pfEntry + grow + vsP, vsP, vsP >= 0 ? '#2F8A6F' : '#B3261E'],
    ['Marked value', M(pfEV), 0, pfEV, '#22272E'],
  ];

  const lastD = (h: Holding) => (h.drift.length ? h.drift[h.drift.length - 1] : null);
  const pins: PfPin[] = [];
  const geo = new Map((board.data?.deals ?? []).map((d) => [d.prospectId, d]));
  p.deals.forEach((x) => {
    const g = geo.get(x.id);
    if (!g || g.lat == null || g.lng == null) return;
    const h = H.find((y) => y.x === x);
    const d = h ? lastD(h) : null;
    pins.push({
      lat: g.lat,
      lng: g.lng,
      own: !!h,
      label: h ? h.letter : '',
      title: (h ? 'Target ' + h.letter : x.name) + ' · ' + (x.city ? x.city + ', ' : '') + x.stt + (h ? ' · owned' : ' · pipeline'),
      col: !h ? '#5B6B7C' : d === null ? '#5B6B7C' : d >= 1 ? '#1F6B52' : d < 0.97 ? '#B3261E' : '#2F5C97',
      r: 3 + x.beds / 60,
    });
  });

  const strip: [string, string, string][] = [
    ['Total beds', String(pfBeds), 'across the book'],
    ['Blended occupancy', pc(pfOcc), occupancyNote(bench.data) ?? 'no US figure on file'],
    ['Aggregate EBITDA', M(pfEbitda), 'modeled from CMS'],
    ['Capital deployed', M(pfEntry), 'at entry'],
    ['Blended multiple', (pfEntry / (pfEbitda || 1)).toFixed(1) + 'x', 'entry EV / EBITDA'],
  ];
  const anon = 'Named targets stay in the operator deal desk. Here they read as descriptors, because a shared console that names acquisition targets is a leak and an embarrassment if a target sees it.';
  const grades = (['A', 'B', 'C', 'D'] as const).map((g) => ({ g, n: held.filter((x) => x.grade === g).length }));
  const conc: [string, string][] = [
    ['Medicaid exposure', pc(sum((h) => h.x.mcaid * h.x.beds) / (pfBeds || 1))],
    ['Beds below peer staffing', String(belowPeer.reduce((s, h) => s + h.x.beds, 0))],
    ['Unrealized value', (pfEV - pfEntry >= 0 ? '+' : '') + M(pfEV - pfEntry) + ' · latest quarterly mark'],
  ];
  const returns: [string, string][] = [
    ['Projected MOIC', (sum((h) => h.x.moic * h.x.entry) / (pfEntry || 1)).toFixed(2) + 'x'],
    ['Projected IRR', pc(sum((h) => h.x.irr * h.x.entry) / (pfEntry || 1))],
    ['Value-creation potential', '+' + M(sum((h) => ((Math.max(0, 0.95 - h.x.occ) * h.x.beds * 365 * h.x.ppd * 0.65) / 1e6) * h.x.mult))],
  ];
  const debt: [string, string][] = [
    ['Debt outstanding', M(debtT)],
    ['Debt service / yr', M(dsT)],
    ['Blended debt cover', (eT / (dsT || 1)).toFixed(2) + '×'],
  ];
  const planLine = planET ? 'Plan vs actual EBITDA ' + (eT >= planET ? '+' : MINUS) + Math.abs((eT / planET - 1) * 100).toFixed(1) + '% across the book' : '';
  const legend = (bg: string, label: string) => (
    <span style={sx('display:inline-flex;align-items:center;gap:5px;font-size:14px;color:#54606C;')}>
      <span style={sx('width:9px;height:9px;border-radius:2px;background:' + bg + ';')} />
      {label}
    </span>
  );
  const dot = (css: string, label: string) => (
    <span style={sx('display:inline-flex;align-items:center;gap:5px;font-size:14px;color:#54606C;')}>
      <span style={sx('width:8px;height:8px;border-radius:50%;' + css)} />
      {label}
    </span>
  );

  return (
    <div data-testid="intel-portfolio" style={sx('display:flex;flex-direction:column;gap:13px;min-width:0;')}>
      <div style={sx('display:flex;align-items:flex-end;gap:16px;flex-wrap:wrap;')}>
        <div>
          <div style={sx('font-size:21px;font-weight:700;letter-spacing:-.018em;')}>Roll-up portfolio</div>
          <div style={sx('font-size:16px;color:#54606C;')}>{held.length + ' facilit' + (held.length === 1 ? 'y' : 'ies') + ' across ' + pfStates + ' states'}</div>
        </div>
        <span style={sx('flex:1;')} />
        <div style={sx('text-align:right;')}>
          <div style={sx('font-size:26px;font-weight:700;letter-spacing:-.025em;')}>{M(pfEV)}</div>
          <div style={sx(CAPS)}>Marked value · MODELED</div>
        </div>
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(168px,1fr));gap:11px;')}>
        {strip.map(([l, v, n]) => (
          <div key={l} style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;')}>
            <div style={sx(CAPS + 'margin-bottom:5px;')}>{l}</div>
            <div style={sx('font-size:20px;font-weight:700;letter-spacing:-.02em;')}>{v}</div>
            <div style={sx('font-size:14px;color:#54606C;')}>{n}</div>
          </div>
        ))}
      </div>
      <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:11px 2px 0;overflow-x:auto;')}>
        <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;margin-bottom:7px;')}>
          <span title={anon} style={sx(CAPS)}>Holdings</span>
          <span title={anon} style={sx('font-size:14px;color:#54606C;')}>names hidden outside the deal desk</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-size:14px;color:#3C454F;font-weight:600;')}>{planLine}</span>
          <BuildLabel><span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;background:rgba(34,39,46,.1);border-radius:4px;padding:1px 5px;')}>MODELED</span></BuildLabel>
        </div>
        <div style={sx('min-width:1040px;')}>
          <div style={sx(COLS + 'font-size:14px;letter-spacing:.08em;text-transform:uppercase;white-space:nowrap;color:#54606C;font-weight:600;padding-bottom:7px;border-bottom:1px solid rgba(34,39,46,.1);')}>
            <span>Facility</span>
            <span style={sx('text-align:right;')}>Beds</span>
            <span style={sx('text-align:right;')}>Occ</span>
            <span style={sx('text-align:center;')}>Grade</span>
            <span style={sx('text-align:right;')}>EBITDA</span>
            <span style={sx('text-align:right;')}>Entry</span>
            <span style={sx('text-align:right;')}>Mark</span>
            <span style={sx('text-align:right;')}>Gain</span>
            <span style={sx('text-align:right;')}>vs plan</span>
            <span style={sx('text-align:right;')}>Cover</span>
            <span style={sx('text-align:right;')}>IRR</span>
            <span>6 qtrs</span>
            <span />
          </div>
          {H.map((h) => {
            const x = h.x;
            const d = lastD(h);
            const num = 'text-align:right;font-variant-numeric:tabular-nums;font-size:14px;';
            return (
              <div key={x.id} data-testid="pf-row" style={sx(COLS + 'align-items:center;padding:8px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
                <span style={sx('font-size:16px;font-weight:600;min-width:0;overflow-wrap:break-word;')}>{'Target ' + h.letter + ' · ' + x.beds + '-bed ' + (x.type || 'SNF') + ', ' + x.stt}</span>
                <span style={sx(num)}>{String(x.beds)}</span>
                <span style={sx(num)}>{pc(x.occ)}</span>
                <span style={sx('text-align:center;')}>
                  <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;font-size:14px;font-weight:700;color:#fff;background:' + GC[x.grade] + ';')}>{x.grade}</span>
                </span>
                <span style={sx(num)}>{M(x.ebitda)}</span>
                <span style={sx(num)}>{M(x.entry)}</span>
                <span style={sx(num + 'font-weight:700;')}>{M(h.mark)}</span>
                <span style={sx(num + 'font-weight:600;color:' + (h.mark >= x.entry ? '#1F6B52' : '#B3261E') + ';')}>{(h.mark >= x.entry ? '+' : MINUS) + M(Math.abs(h.mark - x.entry))}</span>
                <span title={d === null ? 'Marked at entry until a newer cost report re-marks it' : undefined} style={sx(num + 'font-weight:700;color:' + (d === null ? '#54606C' : d >= 1 ? '#1F6B52' : d < 0.97 ? '#B3261E' : '#5B6B7C') + ';')}>
                  {d === null ? 'at entry' : (d >= 1 ? '+' : MINUS) + Math.abs((d - 1) * 100).toFixed(1) + '%'}
                </span>
                <span style={sx(num)}>{(h.actE / x.ds).toFixed(2) + '×'}</span>
                <span style={sx(num)}>{(x.irr * 100).toFixed(0) + '%'}</span>
                <span title="Realized vs modeled, each quarter since entry" style={sx('display:flex;align-items:flex-start;gap:3px;height:26px;border-top:0;background:linear-gradient(rgba(34,39,46,.18),rgba(34,39,46,.18)) 0 13px/100% 1px no-repeat;')}>
                  {h.drift.map((q, i) => {
                    const hh = Math.max(2, Math.min(12, Math.round(Math.abs(q - 1) * 260)));
                    return <span key={i} style={sx('flex:0 0 6px;width:6px;border-radius:1px;height:' + hh + 'px;margin-top:' + (q >= 1 ? 13 - hh : 13) + 'px;background:' + (q >= 1 ? '#2F8A6F' : '#B3261E') + ';')} />;
                  })}
                </span>
                <span style={sx('display:flex;gap:5px;justify-content:flex-end;')}>
                  <a href="#" className="intel-h-out" onClick={(e) => { e.preventDefault(); goF(h, 'target')(); }} style={sx(BTN_O)}>Analysis</a>
                  <a href="#" className="intel-h-solid" onClick={(e) => { e.preventDefault(); goF(h, 'simulate')(); }} style={sx(BTN_S)}>Track</a>
                </span>
              </div>
            );
          })}
          <div style={sx('display:flex;align-items:baseline;gap:9px;padding:9px 0 2px;')}>
            <span style={sx('font-size:16px;font-weight:700;')}>Total marked value</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:16px;font-weight:700;')}>{M(pfEV)}</span>
          </div>
        </div>
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:18px;')}>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:11px 2px;grid-column:span 2;min-width:0;')}>
          <div style={sx('display:flex;align-items:baseline;gap:9px;margin-bottom:6px;')}>
            <span style={sx(CAPS)}>Needs attention</span>
            <span style={sx('font-size:14px;color:#54606C;')}>{(att.length ? att.length + ' open' : 'none open') + ' · worst first'}</span>
          </div>
          {att.length === 0 && <div style={sx('font-size:16px;color:#1F6B52;padding:8px 0;')}>Every holding is on plan, staffed at or above its peers and covering its debt.</div>}
          {att.map((a, i) => (
            <div key={i} style={sx('display:flex;align-items:flex-start;gap:10px;padding:7px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
              <span style={sx('flex:0 0 8px;width:8px;height:8px;border-radius:50%;margin-top:5px;background:' + (a[0] === 2 ? '#B3261E' : '#5B6B7C') + ';')} />
              <span style={sx('flex:0 0 66px;font-size:16px;font-weight:700;')}>{a[1]}</span>
              <span style={sx('flex:1;min-width:0;font-size:16px;color:#3C454F;')}>{a[2]}</span>
              <a href="#" className="intel-h-out" onClick={(e) => { e.preventDefault(); a[4](); }} style={sx('flex:0 0 auto;height:26px;display:inline-flex;align-items:center;padding:0 10px;border-radius:6px;border:1px solid rgba(47,92,151,.35);font-size:14px;font-weight:600;color:#2F5C97;text-decoration:none;white-space:nowrap;')}>
                <span>{a[3]}</span>{' →'}
              </a>
            </div>
          ))}
        </div>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:11px 2px;min-width:0;')}>
          <div style={sx(CAPS + 'margin-bottom:8px;')}>Grade and concentration</div>
          {grades.map(({ g, n }) => (
            <div key={g} style={sx('display:flex;align-items:center;gap:10px;margin-bottom:5px;')}>
              <span style={sx('width:14px;font-size:14px;font-weight:700;color:#3C454F;')}>{g}</span>
              <span style={sx('flex:1;height:7px;border-radius:4px;background:rgba(34,39,46,.07);')}>
                <span style={sx('display:block;height:7px;border-radius:4px;background:' + GC[g] + ';width:' + (held.length ? (n / held.length) * 100 : 0) + '%;')} />
              </span>
              <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;')}>{String(n)}</span>
            </div>
          ))}
          {conc.map(([l, v]) => (
            <div key={l} style={sx(KV)}>
              <span style={sx('color:#3C454F;')}>{l}</span>
              <span style={sx('flex:1;')} />
              <span style={sx('font-variant-numeric:tabular-nums;font-weight:600;text-align:right;')}>{v}</span>
            </div>
          ))}
          <div style={sx('font-size:14px;color:#54606C;padding-top:6px;')}>{held.map((x) => x.stt + ' ' + x.beds).join(' · ') + ' beds'}</div>
        </div>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:11px 2px;min-width:0;')}>
          <div style={sx(CAPS + 'margin-bottom:8px;')}>Returns and debt</div>
          {returns.map(([l, v]) => (
            <div key={l} style={sx(KV)}>
              <span style={sx('color:#3C454F;')}>{l}</span>
              <span style={sx('flex:1;')} />
              <span style={sx('font-variant-numeric:tabular-nums;font-weight:600;color:#1F6B52;')}>{v}</span>
            </div>
          ))}
          {debt.map(([l, v]) => (
            <div key={l} style={sx(KV)}>
              <span style={sx('color:#3C454F;')}>{l}</span>
              <span style={sx('flex:1;')} />
              <span style={sx('font-variant-numeric:tabular-nums;font-weight:600;')}>{v}</span>
            </div>
          ))}
          <div style={sx('font-size:14px;color:#54606C;padding-top:6px;')}>Bank loan · 60% LTV · 7.25% · 25-yr</div>
        </div>
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:18px;')}>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:11px 2px;min-width:0;')}>
          <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;margin-bottom:8px;')}>
            <span style={sx(CAPS)}>Book value by quarter</span>
            <span style={sx('flex:1;')} />
            {legend('rgba(47,92,151,.28)', 'plan')}
            {legend('#2F8A6F', 'marked, above plan')}
            {legend('#B3261E', 'below plan')}
          </div>
          {Q.length === 0 ? (
            <div style={sx('font-size:16px;color:#54606C;padding:8px 0;')}>No quarterly mark yet: every holding is marked at entry until a newer cost report re-marks it.</div>
          ) : (
            <div style={sx('display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:12px;')}>
              {Q.map((r, i) => (
                <div key={i} style={sx('display:flex;flex-direction:column;gap:3px;min-width:0;')}>
                  <span style={sx('font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + (r.real >= r.mod ? '#1F6B52' : '#B3261E') + ';')}>{(r.real >= r.mod ? '+' : MINUS) + M(Math.abs(r.real - r.mod))}</span>
                  <div style={sx('display:flex;align-items:flex-end;gap:3px;height:64px;border-bottom:1px solid rgba(34,39,46,.18);')}>
                    <span style={sx('flex:1;border-radius:3px 3px 0 0;background:rgba(47,92,151,.28);height:' + ht(r.mod) + ';')} />
                    <span style={sx('flex:1;border-radius:3px 3px 0 0;background:' + (r.real >= r.mod ? '#2F8A6F' : '#B3261E') + ';height:' + ht(r.real) + ';')} />
                  </div>
                  <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;line-height:1.3;')}>
                    <span>{r.label}</span>
                    <br />
                    <span>{M(r.real)}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:11px 2px;min-width:0;')}>
          <div style={sx(CAPS + 'margin-bottom:10px;')}>Value bridge · entry to today</div>
          {bridge.map(([l, v, left, w, c]) => (
            <div key={l} style={sx('display:grid;grid-template-columns:150px minmax(0,1fr) 70px;gap:10px;align-items:center;padding:3px 0;')}>
              <span style={sx('font-size:16px;color:#3C454F;white-space:nowrap;')}>{l}</span>
              <span style={sx('position:relative;height:16px;background:rgba(34,39,46,.04);border-radius:3px;')}>
                <span style={sx('position:absolute;top:0;bottom:0;border-radius:3px;background:' + c + ';left:' + ((left / bmax) * 100).toFixed(1) + '%;width:' + bw(w) + ';')} />
              </span>
              <span style={sx('text-align:right;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;')}>{v}</span>
            </div>
          ))}
          <div style={sx('font-size:14px;color:#54606C;padding-top:4px;')}>Marked each quarter at EBITDA × entry multiple · MODELED until cost reports load</div>
        </div>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:11px 2px;min-width:0;display:flex;flex-direction:column;')}>
          <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;margin-bottom:6px;')}>
            <span style={sx(CAPS)}>Where we own</span>
            <span style={sx('flex:1;')} />
            {dot('background:#1F6B52;', 'on plan')}
            {dot('background:#2F5C97;', 'within 3%')}
            {dot('background:#B3261E;', 'behind 3%+')}
            {dot('border:1.5px dashed #5B6B7C;box-sizing:border-box;', 'pipeline')}
          </div>
          <PortfolioMap pins={pins} />
        </div>
      </div>
    </div>
  );
}
